import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/config';
import { useChat } from '../../contexts/ChatContext';
import { getGroupCandidates } from '../../services/moodle/chat';
import GroupComposer from './GroupComposer';

// Container: owns candidate fetch, selection state, name state, 40-cap
// enforcement, submit, and error handling. Mirrors BroadcastComposerContainer
// — candidates are fetched directly from the service (not through
// ChatContext.canCreateGroup, which only carries the boolean gate).
const GroupComposerContainer = ({ courseId, onBack }) => {
  const { user } = useAuth();
  const { config } = useConfig();
  const { createGroupChat } = useChat();

  const [students, setStudents] = useState([]);
  const [maxMembers, setMaxMembers] = useState(40);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadCandidates = async () => {
      if (!user || !user.token || !courseId) return;
      setCandidatesLoading(true);
      try {
        const data = await getGroupCandidates(user.token, config.baseURL, courseId);
        if (cancelled) return;
        setStudents(data.students || []);
        setMaxMembers(data.maxmembers || 40);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading group candidates:', err);
        setError('No se pudieron cargar los alumnos. Intenta nuevamente.');
      } finally {
        if (!cancelled) setCandidatesLoading(false);
      }
    };

    loadCandidates();
    return () => {
      cancelled = true;
    };
  }, [user, config, courseId]);

  const handleToggleRecipient = useCallback((id) => {
    setSelectedIds(prev => (
      prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
    ));
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(students.map(s => s.id));
  }, [students]);

  const handleClearAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleCreate = async () => {
    if (!name.trim() || selectedIds.length === 0 || selectedIds.length > maxMembers) return;
    setCreating(true);
    setError(null);
    try {
      await createGroupChat(courseId, name.trim(), selectedIds);
      setSuccessMessage('Grupo creado');
      // On success: brief confirmation, then auto-return to the contacts list.
      setTimeout(() => {
        onBack();
      }, 1200);
    } catch (err) {
      console.error('Error creating group:', err);
      // Keep composer open, preserve selection + name so the teacher doesn't
      // have to redo it — only surface the error.
      setError(err.message || 'No se pudo crear el grupo. Intenta nuevamente.');
    } finally {
      setCreating(false);
    }
  };

  if (successMessage) {
    return (
      <div className="w-full flex flex-col h-full items-center justify-center gap-3 p-6 text-center">
        <div className="text-2xl">✅</div>
        <p className="text-xl font-semibold">{successMessage}</p>
      </div>
    );
  }

  return (
    <GroupComposer
      onBack={onBack}
      name={name}
      onNameChange={setName}
      students={students}
      recipientsLoading={candidatesLoading}
      selectedIds={selectedIds}
      onToggleRecipient={handleToggleRecipient}
      onSelectAll={handleSelectAll}
      onClearAll={handleClearAll}
      maxMembers={maxMembers}
      creating={creating}
      error={error}
      onCreate={handleCreate}
    />
  );
};

export default GroupComposerContainer;
